/**
 * Reports: campaign outcomes (plan docs/plans/lms-plan.md 13 tab 6, 8.2;
 * Appendix A GET /admin/reports/campaigns/:id; 18.21, 18.23; R8, R23; WP-45).
 *
 * Pick a send (GET /admin/campaigns, newest first; WP-43's client) and see
 * what it did and where each person stands now: the send outcomes (assigned,
 * already assigned, already current, skipped), the progress since (completed,
 * on time, open, overdue, waived, cancelled), the completion and on-time
 * rates, and one row per person with the send outcome and their state now.
 * People the admin does not reach are counted, never named (G14, 18.21).
 */
import { useState } from 'react';
import { Send } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import { listCampaigns } from '../../../api/campaigns';
import { getCampaignReport, RECIPIENT_STATUSES } from '../../../api/reports';
import type { RecipientStatus } from '../../../api/reports';
import { Pill } from '../../../components/StatusPill';
import type { PillTone } from '../../../components/StatusPill';
import {
  cardCls,
  formatDay,
  labelCls,
  metaCls,
  microLabelCls,
  OUTCOME_LABELS,
  OutcomePill,
  personName,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LeaverTag, LoadFailed, Loading, useAsync } from '../matrix/shared';
import { rateText } from './CompletionRatesPanel';

export const RECIPIENT_STATUS_WORDS: Record<RecipientStatus, { word: string; tone: PillTone }> = {
  completed: { word: 'Completed', tone: 'green' },
  open: { word: 'Open', tone: 'primary' },
  overdue: { word: 'Overdue', tone: 'red' },
  waived: { word: 'Waived', tone: 'gray' },
  cancelled: { word: 'Cancelled', tone: 'gray' },
  already_current: { word: 'Already current', tone: 'green' },
  skipped_inactive: { word: 'Skipped, inactive', tone: 'gray' },
  no_assignment: { word: 'No assignment', tone: 'gray' },
};

const KIND_WORDS: Record<string, string> = {
  manual: 'Sent by hand',
  scenario: 'Scenario',
  sop_update: 'SOP update',
  retrain: 'Retrain',
  acknowledge: 'Acknowledge',
};

function Stat({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className={cardCls}>
      <div className={microLabelCls}>{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function CampaignReportPanel() {
  const [campaignId, setCampaignId] = useState('');
  const campaigns = useAsync(() => listCampaigns({ limit: 100 }), 'campaigns', 'The campaigns could not be loaded.');
  const report = useAsync(() => getCampaignReport(campaignId), campaignId, 'The campaign report could not be loaded.', Boolean(campaignId));
  const list = campaigns.data?.items ?? [];
  const data = report.data;

  return (
    <div className="space-y-4" data-testid="report-campaigns">
      <div>
        <span className={labelCls}>Campaign</span>
        <FilterSelect label="Campaign" value={campaignId} onChange={setCampaignId} auto disabled={campaigns.loading}>
          <option value="">Choose a campaign</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>
              {`${c.course?.title ?? 'Course'}: ${KIND_WORDS[c.kind] ?? c.kind}, sent ${formatDay(c.sent_at)} to ${c.recipient_count.toLocaleString()}`}
            </option>
          ))}
        </FilterSelect>
      </div>

      {campaigns.error ? <LoadFailed message={campaigns.error} onRetry={campaigns.reload} /> : null}
      {report.error ? <LoadFailed message={report.error} onRetry={report.reload} testId="campaign-report-error" /> : null}
      {!campaignId ? (
        <EmptyState
          icon={Send}
          variant="card"
          title={campaigns.data && list.length === 0 ? 'No campaign has been sent yet' : 'Choose a campaign'}
          hint="See what a send did and where each person stands now."
        />
      ) : null}
      {campaignId && report.loading && !data ? <Loading rows={4} /> : null}

      {data ? (
        <>
          <section className={cardCls} aria-label="The send">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {`${data.campaign.course?.title ?? 'Course'} (${KIND_WORDS[data.campaign.kind] ?? data.campaign.kind})`}
            </p>
            <p className={metaCls}>
              {`Sent by ${data.campaign.sent_by.full_name ?? 'an admin'} on ${formatDay(data.campaign.sent_at)}, due ${formatDay(data.campaign.due_at)}${data.campaign.force ? ', also to people already current' : ''}.`}
            </p>
            {data.campaign.message ? <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">{data.campaign.message}</p> : null}
          </section>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Recipients" value={data.campaign.recipient_count.toLocaleString()} />
            <Stat label="Completion rate" value={rateText(data.completion_rate_pct)} testId="campaign-completion-rate" />
            <Stat label="On-time rate" value={rateText(data.on_time_rate_pct)} testId="campaign-on-time-rate" />
            <Stat label="Completed on time" value={data.progress.completed_on_time.toLocaleString()} />
          </div>

          <div className="flex flex-wrap items-center gap-1.5" data-testid="campaign-outcomes">
            <span className={microLabelCls}>At send</span>
            {(Object.keys(OUTCOME_LABELS) as Array<keyof typeof OUTCOME_LABELS>).map((o) => (
              <span key={o} className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
                <OutcomePill outcome={o} />
                <span className="tabular-nums">{(data.outcomes[o] ?? 0).toLocaleString()}</span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="campaign-progress">
            <span className={microLabelCls}>Now</span>
            {RECIPIENT_STATUSES.filter((s) => (data.progress[s] ?? 0) > 0).map((s) => (
              <span key={s} className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
                <Pill tone={RECIPIENT_STATUS_WORDS[s].tone}>{RECIPIENT_STATUS_WORDS[s].word}</Pill>
                <span className="tabular-nums">{data.progress[s].toLocaleString()}</span>
              </span>
            ))}
          </div>

          {data.recipients.length > 0 ? (
            <div className={tableCardCls}>
              <table className={tableCls} data-testid="campaign-recipients">
                <thead className={theadCls}>
                  <tr>
                    <th scope="col" className={thCls}>
                      Person
                    </th>
                    <th scope="col" className={thCls}>
                      At send
                    </th>
                    <th scope="col" className={thCls}>
                      Now
                    </th>
                    <th scope="col" className={thCls}>
                      Due
                    </th>
                    <th scope="col" className={thCls}>
                      On time
                    </th>
                  </tr>
                </thead>
                <tbody className={tbodyCls}>
                  {data.recipients.map((r) => {
                    const now = RECIPIENT_STATUS_WORDS[r.status] ?? { word: r.status, tone: 'gray' as PillTone };
                    return (
                      <tr key={r.user_id} data-testid={`campaign-recipient-${r.user_id}`}>
                        <td className={tdCls}>
                          <div className="font-medium">
                            {personName(r)}
                            <LeaverTag deleted={r.deleted} />
                          </div>
                          {r.email && r.full_name ? <div className={metaCls}>{r.email}</div> : null}
                        </td>
                        <td className={tdCls}>
                          <OutcomePill outcome={r.outcome} />
                        </td>
                        <td className={tdCls}>
                          <Pill tone={now.tone}>{now.word}</Pill>
                        </td>
                        <td className={tdCls}>{r.assignment ? formatDay(r.assignment.due_at) : '\u2014'}</td>
                        <td className={tdCls}>{r.on_time === null ? '\u2014' : r.on_time ? 'Yes' : 'No'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {data.hidden_recipient_count > 0 ? (
            <p className={metaCls} data-testid="campaign-hidden">
              {`${data.hidden_recipient_count.toLocaleString()} more ${data.hidden_recipient_count === 1 ? 'person' : 'people'} at sites you do not administer received this send; they are not listed or counted here.`}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default CampaignReportPanel;
